import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Buildings, House, FileText, ChatCircleDots, User, SignOut, List, X, Gauge } from '@phosphor-icons/react';
import LegalFooter from './LegalFooter';

export default function TenantLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/portal', icon: House, label: 'Übersicht', end: true },
    { to: '/portal/abrechnung', icon: FileText, label: 'Nebenkosten' },
    { to: '/portal/zaehler', icon: Gauge, label: 'Zählerstände' },
    { to: '/portal/dokumente', icon: FileText, label: 'Dokumente' },
    { to: '/portal/messages', icon: ChatCircleDots, label: 'Nachrichten' },
    { to: '/portal/profile', icon: User, label: 'Mein Profil' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="tenant-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-emerald-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Buildings weight="bold" className="w-7 h-7" />
          <span className="font-heading text-base font-black tracking-tight">MIETER-PORTAL</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-white hover:bg-emerald-800 rounded-lg"
          data-testid="tenant-mobile-menu-toggle"
        >
          {sidebarOpen ? <X weight="bold" className="w-6 h-6" /> : <List weight="bold" className="w-6 h-6" />}
        </button>
      </header>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed top-0 left-0 h-full z-50 bg-emerald-900 text-white flex flex-col transition-transform duration-300 w-72 lg:w-64
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static`}
        data-testid="tenant-sidebar"
      >
        <div className="hidden lg:flex p-6 border-b border-emerald-800 items-center gap-3">
          <Buildings weight="bold" className="w-8 h-8 text-white flex-shrink-0" />
          <div>
            <span className="font-heading text-base font-black tracking-tight block">MIETER</span>
            <span className="font-heading text-base font-black tracking-tight block">PORTAL</span>
          </div>
        </div>

        <div className="lg:hidden flex items-center justify-between p-4 border-b border-emerald-800">
          <span className="font-heading text-lg font-bold">Menü</span>
          <button onClick={() => setSidebarOpen(false)} className="p-2 text-emerald-200 hover:text-white">
            <X weight="bold" className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3.5 transition-colors rounded-lg ${
                  isActive ? 'bg-emerald-800 text-white' : 'text-emerald-200 hover:text-white hover:bg-emerald-800'
                }`
              }
              data-testid={`tenant-nav-${item.label.toLowerCase()}`}
            >
              <item.icon weight="bold" className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-emerald-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-700 flex items-center justify-center text-white font-bold rounded-lg">
              {user?.name?.charAt(0).toUpperCase() || 'M'}
            </div>
            <div className="overflow-hidden flex-1">
              <p className="font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-emerald-200 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-emerald-200 hover:text-white hover:bg-emerald-800 transition-colors rounded-lg"
            data-testid="tenant-logout-button"
          >
            <SignOut weight="bold" className="w-5 h-5" />
            <span className="font-medium">Abmelden</span>
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen flex flex-col min-w-0">
        <div className="pt-16 lg:pt-0 p-4 sm:p-6 lg:p-8 flex-1 min-w-0 overflow-x-hidden">{children}</div>
        <LegalFooter />
      </main>
    </div>
  );
}
